import { AlertTriangle, Sparkles } from "lucide-react";
import type { AIAnalysis } from "../../core";

export default function AIRecommendations({ analysis, loading }: { analysis: AIAnalysis | null; loading: boolean }) {
	if (loading) return <div className="h-64 rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 animate-pulse mt-6" />;

	const getActionStyles = (action: string) => {
		const act = action?.toLowerCase() || "";
		if (act.includes("buy")) {
			return "text-emerald-700 bg-emerald-100 border-emerald-300 dark:text-emerald-400 dark:bg-emerald-400/10 dark:border-emerald-400/20";
		} else if (act.includes("sell")) {
			return "text-rose-700 bg-rose-100 border-rose-300 dark:text-rose-400 dark:bg-rose-400/10 dark:border-rose-400/20";
		} else if (act.includes("trim")) {
			return "text-orange-700 bg-orange-100 border-orange-300 dark:text-orange-400 dark:bg-orange-400/10 dark:border-orange-400/20";
		} else if (act.includes("average down")) {
			return "text-blue-700 bg-blue-100 border-blue-300 dark:text-blue-400 dark:bg-blue-400/10 dark:border-blue-400/20";
		} else if (act.includes("profit take")) {
			return "text-violet-700 bg-violet-100 border-violet-300 dark:text-violet-400 dark:bg-violet-400/10 dark:border-violet-400/20";
		} else {
			return "text-zinc-600 bg-zinc-200 border-zinc-300 dark:text-zinc-400 dark:bg-zinc-800 dark:border-zinc-700"; // Hold
		}
	};

	const warnings = analysis?.sector_exposure_warnings || [];
	const recommendations = analysis?.recommendations || [];
	const hasWarnings = warnings.length > 0;
	const hasRecommendations = recommendations.length > 0;

	return (
		<div className="rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 p-6 shadow-md">
			<h3 className="text-xl font-semibold mb-6">Actionable Intelligence</h3>

			{!analysis || (!hasWarnings && !hasRecommendations) ? (
				<div className="flex flex-col items-center justify-center py-10 px-4 text-center border border-dashed border-zinc-300 rounded-lg bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-950/50">
					<Sparkles className="text-zinc-400 dark:text-zinc-600 mb-3" size={32} />
					<p className="text-zinc-700 dark:text-zinc-300 font-medium">No analysis recommendations available yet</p>
					<p className="text-xs text-zinc-500 mt-1 max-w-sm">
						AI portfolio recommendations will appear here once an analysis run is generated for your active positions.
					</p>
				</div>
			) : (
				<>
					{/* Sector Warnings */}
					{hasWarnings && (
						<div className="mb-6 p-4 rounded-lg bg-amber-100 border border-amber-300 dark:bg-amber-500/10 dark:border-amber-500/20">
							<h4 className="flex items-center text-amber-700 dark:text-amber-400 font-medium mb-3 gap-2">
								<AlertTriangle size={18} /> Sector Exposure Warnings
							</h4>
							<ul className="flex flex-col gap-2">
								{warnings.map((warn: string, i: number) => (
									<li key={i} className="flex items-start gap-2 text-sm text-zinc-700 dark:text-zinc-300">
										<span className="text-amber-700 dark:text-amber-400 mt-0.5">•</span>
										<span>{warn}</span>
									</li>
								))}
							</ul>
						</div>
					)}

					{/* Recommendations Grid */}
					{hasRecommendations && (
						<div className="grid gap-4 md:grid-cols-2">
							{recommendations.map((rec, idx: number) => (
								<div
									key={idx}
									className="p-4 rounded-lg bg-zinc-50 border border-zinc-200 dark:bg-zinc-950 dark:border-zinc-800 flex flex-col gap-3"
								>
									<div className="flex justify-between items-start">
										<span className="font-bold text-lg">{rec.ticker}</span>
										<span className={`px-2.5 py-1 rounded border text-xs font-semibold uppercase ${getActionStyles(rec.action)}`}>
											{rec.action}
										</span>
									</div>
									<p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">{rec.reasoning}</p>
								</div>
							))}
						</div>
					)}
				</>
			)}
		</div>
	);
}
